import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { categories, mangas, type Manga } from '@/mocks/manga';

const SORTS = [
  { key: 'latest', label: '最新更新' },
  { key: 'hot', label: '人气最高' },
  { key: 'rating', label: '好评最多' },
];

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  const keyword = (searchParams.get('q') ?? '').trim();
  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name ?? '全部漫画';

  useEffect(() => {
    setPage(1);
  }, [slug, keyword]);

  const filtered = useMemo(() => {
    let list: Manga[] = category
      ? mangas.filter((m) => m.categoryIds.includes(category.id))
      : [...mangas];
    if (keyword) {
      const kw = keyword.toLowerCase();
      list = list.filter(
        (m) =>
          m.title.toLowerCase().includes(kw) ||
          m.author.toLowerCase().includes(kw) ||
          m.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    if (sort === 'hot') list = [...list].sort((a, b) => b.views - a.views);
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const changeSort = (key: string) => {
    setSort(key);
    setPage(1);
  };

  const changePage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => {
    setSearchParams({});
  };

  const seoTitle = keyword
    ? `漫画搜索：${keyword}_073漫画网`
    : `${categoryName}漫画免费阅读_073漫画网-热血都市漫画大全`;

  return (
    <div className="mx-auto max-w-7xl px-4 pb-16 pt-24 md:px-6 md:pt-28">
      <Seo
        title={seoTitle}
        description={
          category?.description ??
          '073漫画网汇集健身、热血、都市、恋爱、搞笑、玄幻、悬疑等题材漫画，全部免费下拉式阅读，每日更新，看漫画就到073漫画网。'
        }
        keywords={`${categoryName}漫画,漫画免费阅读,下拉式漫画,073漫画网`}
        canonicalPath={category ? `/category/${category.slug}` : '/category'}
      />

      <Breadcrumb
        items={[{ label: '漫画分类', to: '/category' }, { label: categoryName }]}
      />

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">
            {keyword ? `"${keyword}" 的搜索结果` : `${categoryName}漫画`}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {category?.description ??
              '精选健身、热血青春、都市职场、恋爱日常、搞笑日常、玄幻修真、悬疑推理等多题材漫画，全部支持免费下拉式阅读。'}
          </p>
        </div>
        {keyword && (
          <button
            type="button"
            onClick={clearSearch}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-close-circle-line" aria-hidden="true" />
            清除搜索“{keyword}”（共{filtered.length}部）
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Link
          to="/category"
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
            !category ? 'bg-primary-500 font-medium text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
          }`}
        >
          全部
        </Link>
        {categories.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.slug}`}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              category?.slug === cat.slug
                ? 'bg-primary-500 font-medium text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            {cat.name}
          </Link>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-foreground-600 md:text-sm">
          共 <strong className="text-primary-700">{filtered.length}</strong> 部作品
          {keyword && <span> · 搜索“{keyword}”</span>}
        </p>
        <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => changeSort(s.key)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                sort === s.key ? 'bg-background-50 font-medium text-primary-700 shadow-none' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {pageItems.map((manga) => (
            <MangaCard key={manga.id} manga={manga} />
          ))}
        </div>
      ) : (
        <div className="mt-16 flex flex-col items-center gap-3 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-search-line" aria-hidden="true" />
          </span>
          <p className="text-sm text-foreground-600">没有找到相关漫画，换个关键词试试吧～</p>
          <Link
            to="/category"
            className="mt-2 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            浏览全部漫画
          </Link>
        </div>
      )}

      {totalPages > 1 && (
        <nav aria-label="分页导航" className="mt-10 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => changePage(current - 1)}
            disabled={current === 1}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => changePage(p)}
              aria-current={p === current ? 'page' : undefined}
              className={`h-9 min-w-9 whitespace-nowrap rounded-md px-2 text-sm transition-colors ${
                p === current
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => changePage(current + 1)}
            disabled={current === totalPages}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
          </button>
        </nav>
      )}

      <div className="mt-12 rounded-lg bg-background-100 p-5 md:p-6">
        <h2 className="text-sm font-semibold text-foreground-950 md:text-base">
          {categoryName}漫画推荐，免费下拉式阅读就在073漫画网
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-foreground-700 md:text-sm">
          {categoryName}漫画是073漫画网的一大特色板块。这里汇集了
          {category?.name ?? '健身、热血、都市'}题材最优质的漫画作品，
          {category?.name ?? '涵盖多种画风与剧情'}
          ，支持手机端与电脑端下拉式流畅阅读。无论是《健身教练》这样燃点满满的健身热血漫画，还是轻松搞笑的日常作品，073漫画网都能让你看得过瘾。漫画免费阅读、每日更新、无弹窗干扰，快选择你感兴趣的作品开始阅读吧！
        </p>
      </div>
    </div>
  );
}